import {
  addToast,
  Button,
  Checkbox,
  Dialogue,
  Fieldset,
  Form,
  Label,
  PasswordField,
  Text
} from '@albert-io/atomic';
import type {PropsWithChildren} from 'react';
import React, {useEffect} from 'react';

import type {AppError} from 'lib/normalizeError';
import {useResetPasswordStore} from './ResetPassword.store';

/** Shared password fields and validation for reset and change-password forms. */

interface NewPasswordFieldProps {
  [key: string]: any;
}

const NewPasswordField = (props: NewPasswordFieldProps) => {
  const inputType = useResetPasswordStore((state) => state.inputType);
  const handlePasswordChange = useResetPasswordStore((state) => state.handlePasswordChange);
  const isPasswordValid = useResetPasswordStore((state) => state.isPasswordValid);
  const passwordInputValue = useResetPasswordStore((state) => state.passwordInputValue);

  const hasError = !!(passwordInputValue && !isPasswordValid);
  return (
    <PasswordField
      className='mb-x2'
      error={hasError}
      label='New password'
      message={
        hasError
          ? 'Password must have at least 8 characters and at least one number or special character.'
          : ''
      }
      onChange={handlePasswordChange}
      type={inputType}
      value={passwordInputValue}
      {...props}
    />
  );
};

interface ConfirmPasswordFieldProps {
  [key: string]: any;
}

const ConfirmPasswordField = (props: ConfirmPasswordFieldProps) => {
  const confirmInputValue = useResetPasswordStore((state) => state.confirmInputValue);
  const doPasswordsMatch = useResetPasswordStore((state) => state.doPasswordsMatch);
  const handleConfirmPasswordChange = useResetPasswordStore(
    (state) => state.handleConfirmPasswordChange
  );
  const inputType = useResetPasswordStore((state) => state.inputType);

  const hasError = !!(confirmInputValue && !doPasswordsMatch);
  return (
    <PasswordField
      className='mb-x2'
      error={hasError}
      label='Confirm new password'
      message={hasError ? 'Passwords do not match' : ''}
      onChange={handleConfirmPasswordChange}
      type={inputType}
      value={confirmInputValue}
      {...props}
    />
  );
};

const ShowPasswordToggle = () => {
  const setInputType = useResetPasswordStore((state) => state.setInputType);
  return (
    <Label>
      <Checkbox aria-hidden={false} className='mr-x2' onChange={setInputType} />
      Show password
    </Label>
  );
};

const PasswordForm = ({children}: PropsWithChildren<{}>) => {
  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <Fieldset className='flex flex-col'>{children}</Fieldset>
    </Form>
  );
};

const Legend = ({children}: PropsWithChildren<{}>) => (
  <Text as='legend' className='mb-x5' color='secondary'>
    {children}
  </Text>
);

interface ResetPasswordProps extends PropsWithChildrenRequired {
  customDisableSubmitCondition?: boolean;
  onCancel?: () => void;
  onError?: (error: any) => void;
  onSubmit: (password: string) => Promise<void>;
  onSubmitSuccess?: () => void;
  title?: string;
}

const defaultErrorHandler = (_error: AppError) => {
  addToast({
    title: 'Oops! Looks like something went wrong',
    color: 'negative',
    message: 'There was a problem with your request. If the problem persists, contact us.'
  });
};

const ResetPassword = ({
  customDisableSubmitCondition = false,
  onCancel,
  onError = defaultErrorHandler,
  onSubmit,
  onSubmitSuccess = () => {},
  title = 'Reset password',
  children,
  ...rest
}: ResetPasswordProps) => {
  const disableSubmit = useResetPasswordStore((state) => state.disableSubmit);
  const handleSubmit = useResetPasswordStore((state) => state.handleSubmit);
  const setCustomDisableSubmitCondition = useResetPasswordStore(
    (state) => state.setCustomDisableSubmitCondition
  );
  const reset = useResetPasswordStore((state) => state.reset);

  // Sync custom disable condition prop with store
  useEffect(() => {
    setCustomDisableSubmitCondition(customDisableSubmitCondition);
  }, [customDisableSubmitCondition, setCustomDisableSubmitCondition]);

  // Reset store when component unmounts
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const onSubmitClick = async () => {
    await handleSubmit({onSubmit, onError, onSubmitSuccess});
  };

  return (
    <Dialogue title={title} {...rest}>
      <Dialogue.Body>{children}</Dialogue.Body>
      <Dialogue.BtnGroup>
        <Button disabled={disableSubmit} onClick={onSubmitClick}>
          Update password
        </Button>
        {typeof onCancel === 'function' && (
          <Button color='secondary' onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Dialogue.BtnGroup>
    </Dialogue>
  );
};

ResetPassword.ConfirmPasswordField = ConfirmPasswordField;
ResetPassword.Form = PasswordForm;
ResetPassword.NewPasswordField = NewPasswordField;
ResetPassword.ShowPasswordToggle = ShowPasswordToggle;
ResetPassword.Legend = Legend;

export default ResetPassword;
